import React, { useState } from 'react';
import styled from 'styled-components';
import { FormField as FormFieldType, FormValues } from '../types/FormTypes';
import FormField from './FormField';
import SubmitButton from './SubmitButton';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const FormPage = styled.div`
  width: 100%;
  max-width: 600px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 40px;
`;

const FormBody = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormTitle = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: #333333;
  margin-bottom: 32px;
  text-align: center;
`;

const FormActions = styled.div`
  margin-top: 32px;
  display: flex;
  justify-content: center;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 14px;
  text-align: center;
  margin-bottom: 16px;
  padding: 12px;
  background-color: #fdf2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
`;

interface FormContainerProps {
  title: string;
  fields: FormFieldType[];
  submitText: string;
  onSubmit: (values: FormValues) => void;
}

const FormContainer: React.FC<FormContainerProps> = ({
  title,
  fields,
  submitText,
  onSubmit
}) => {
  const [values, setValues] = useState<FormValues>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleInputChange = (fieldId: string, value: string | number | File | null) => {
    setValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
    // Очищаем ошибки при изменении поля
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];
    console.log('Starting form validation...');
    
    fields.forEach((field) => {
      const fieldId = `field_${field.field.id}`;
      const value = values[fieldId];
      console.log(`Validating field ${fieldId}:`, value);
      
      if (!value || 
          (typeof value === 'string' && value.trim() === '') ||
          (value === null) ||
          (value === undefined)) {
        const errorMsg = `Поле "${field.name}" обязательно для заполнения`;
        console.log(`Field ${fieldId} failed validation:`, errorMsg);
        newErrors.push(errorMsg);
      } else {
        console.log(`Field ${fieldId} passed validation`);
      }
    });
    
    console.log('Validation complete. Errors:', newErrors);
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submit triggered');
    console.log('Current form values:', values);
    console.log('Fields:', fields);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }
    
    console.log('Form validation passed, submitting...');
    setIsSubmitting(true);
    
    try {
      await onSubmit(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedFields = [...fields].sort((a, b) => a.order - b.order);
  const isFormValid = fields.every(field => {
    const fieldId = `field_${field.field.id}`;
    const value = values[fieldId];
    return value && 
           (typeof value !== 'string' || value.trim() !== '') &&
           value !== null &&
           value !== undefined;
  });

  return (
    <PageContainer>
      <FormPage>
        <FormTitle>{title}</FormTitle>
        <FormBody onSubmit={handleSubmit} noValidate>
          {errors.length > 0 && (
            <ErrorMessage>
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </ErrorMessage>
          )}
          {sortedFields.map((field) => (
            <FormField
              key={field.field.id}
              field={field}
              value={values[`field_${field.field.id}`] || ''}
              onChange={(value) => handleInputChange(`field_${field.field.id}`, value)}
            />
          ))}
          <FormActions>
            <SubmitButton
              text={submitText}
              isLoading={isSubmitting}
              disabled={isSubmitting || !isFormValid}
            />
          </FormActions>
        </FormBody>
      </FormPage>
    </PageContainer>
  );
};

export default FormContainer;